import { Text } from '@thanx/uikit/text'
import DetailPanel from 'components/DetailPanel'
import FieldGrid, { FieldGridItem } from 'components/FieldGrid'
import type { SurfaceVariant } from 'components/orderFeedbackSurface'
import { buildTranslate } from 'locales'
import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import { useAbility } from 'utilities/ability'
import { currencyFormatter } from 'utilities/formatters'

const t = buildTranslate('components.customer_info')

// Flat view of the customer the record belongs to. Flat (not the RAI
// `ConversationCustomer`) so both wire shapes feed it: RecoveryAI's loyalty
// tier is an object, the order-rating endpoint's is already a string.
export type CustomerInfo = {
  // PII — null without consent, rendered as the em-dash placeholder.
  firstName: string | null
  lastName: string | null
  email: string | null
  phone: string | null
  // Non-identifying aggregates — always present regardless of PII consent.
  loyaltyTier: string | null
  totalVisits: number
  // DOLLARS, not cents: the customer-profile payload this card is fed from on
  // the Reports surface reports lifetime spend in dollars (models/UserProfile,
  // rendered with currencyFormatter by Users/View/Counters), so the RAI caller
  // converts its cents rather than this card guessing a unit.
  lifetimeSpend: number
  // Optional: only the RecoveryAI surface has a resolution history to count —
  // the customer-profile payload carries no equivalent, so it em-dashes.
  priorResolutionCount?: number | null
}

type Props = {
  customer: CustomerInfo
  // Where "View customer profile" navigates. Omitted on a surface with no
  // customer identifier to link to (the RAI case detail, DATA-4871) → the
  // control renders non-functional instead of linking nowhere.
  profileTo?: string | null
  // Which surface this card renders on. The order-rating detail redesign renders
  // card titles at Header4 (18px); the RAI case detail (the default) keeps them
  // at BodyText4 (14px) to stay consistent with its other cards.
  variant?: SurfaceVariant
  // Opens the profile link in a new tab. Defaults to same-tab so the NPS
  // report caller (RatingDetail) is unaffected.
  profileOpensInNewTab?: boolean
}

// The "Customer information" panel shared by the RecoveryAI case detail and the
// Reports order-rating detail page: a 2-col FieldGrid (4 rows × 2) with the
// profile link in the card header.
const CustomerInfoCard = ({
  customer,
  profileTo,
  variant = 'case-detail',
  profileOpensInNewTab,
}: Props) => {
  const [css] = useStyletron()
  const largeHeading = variant === 'order-rating'
  const ability = useAbility()
  // profileTo's destination (/users/:identifier) is gated on its own route
  // ability, independent of whatever let a caller reach this card (RAI's
  // `manage RecoveryAI`, Reports' `access NpsReports`) — permissionMap.ts
  // keeps `members` its own permission group, so a viewer can hold either
  // without the other. Checked once here rather than per caller, so every
  // caller is correct by default.
  const canViewProfile = ability.can('access', 'UsersByIdentifier')
  const href = profileTo && canViewProfile ? profileTo : null

  const items: FieldGridItem[] = useMemo(
    () => [
      { label: t('first_name'), value: customer.firstName ?? t('empty') },
      { label: t('last_name'), value: customer.lastName ?? t('empty') },
      { label: t('email'), value: customer.email ?? t('empty') },
      { label: t('phone'), value: customer.phone ?? t('empty') },
      { label: t('loyalty_tier'), value: customer.loyaltyTier ?? t('empty') },
      { label: t('total_visits'), value: String(customer.totalVisits) },
      {
        label: t('lifetime_spend'),
        // No cents on a lifetime aggregate (matches Figma "$1,240").
        value: currencyFormatter(customer.lifetimeSpend, false),
      },
      {
        label: t('prior_resolutions'),
        value:
          customer.priorResolutionCount != null
            ? String(customer.priorResolutionCount)
            : t('empty'),
      },
    ],
    [customer]
  )

  const profileLinkClass = css({
    border: 'none',
    background: 'none',
    padding: 0,
    color: merchantTheme.colors.belizeHole50,
    // Weight is surface-scoped, not global: the order-rating redesign's mockup
    // wants a non-bold (400) text link, but the RAI case detail keeps its
    // semibold (600) — and this class applies to the FUNCTIONAL link too (the
    // Reports RatingDetail passes a real profileTo), so an unconditional 400
    // would be a live weight regression on both surfaces' active links. Gated on
    // variant so only the redesigned page changes.
    fontWeight: variant === 'order-rating' ? 400 : 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    textDecoration: 'none',
  })

  return (
    <DetailPanel>
      <div className="d-flex align-items-center justify-content-between">
        {largeHeading ? (
          <Text.Header4>{t('heading')}</Text.Header4>
        ) : (
          <Text.BodyText4 bold>{t('heading')}</Text.BodyText4>
        )}
        {href ? (
          <Link
            to={href}
            className={profileLinkClass}
            target={profileOpensInNewTab ? '_blank' : undefined}
            rel={profileOpensInNewTab ? 'noopener noreferrer' : undefined}
            // New-tab is a context change AT users must be warned about
            // before it happens (WCAG 3.2.5) — the aria-hidden icon doesn't
            // convey that non-visually, so the accessible name carries it.
            aria-label={
              profileOpensInNewTab ? t('view_profile_new_tab_hint') : undefined
            }
          >
            {t('view_profile')}
            <span className="fa fa-external-link" aria-hidden="true" />
          </Link>
        ) : (
          // No profile route available (DATA-4871, or the viewer lacks
          // access) — non-functional. aria-disabled (not the `disabled`
          // attribute) keeps it focusable so AT users can reach the hint;
          // no onClick so it does nothing. title mirrors the aria-label so
          // sighted mouse users get the same explanation AT users do.
          <button
            type="button"
            aria-disabled="true"
            aria-label={t('view_profile_disabled_hint')}
            title={t('view_profile_disabled_hint')}
            className={`${profileLinkClass} ${css({
              cursor: 'not-allowed',
            })}`}
          >
            {t('view_profile')}
            <span className="fa fa-external-link" aria-hidden="true" />
          </button>
        )}
      </div>
      <div className={css({ marginTop: '12px' })}>
        <FieldGrid items={items} ariaLabel={t('heading')} />
      </div>
    </DetailPanel>
  )
}

export default CustomerInfoCard
